import {
  dashboardResponseSchema,
  tmallDashboardResponseSchema,
  tmallExperienceDashboardResponseSchema,
  pddDashboardResponseSchema,
  kuaishouDashboardResponseSchema,
  douyinDashboardResponseSchema,
  douyinExperienceDashboardResponseSchema,
  authUserSchema,
  syncStatusSchema,
  type CreateSupervisorInput,
  type DashboardResponse,
  type ImportTmallPerformanceInput,
  type ImportPddPerformanceInput,
  type ImportKuaishouPerformanceInput,
  type PatrolSettings,
  type TmallDashboardResponse,
  type TmallExperienceDashboardResponse,
  type PddDashboardResponse,
  type KuaishouDashboardResponse,
  type DouyinDashboardResponse,
  type DouyinExperienceDashboardResponse,
  type ImportDouyinPerformanceInput,
  type AuthUser,
  type SyncStatus,
  type UpdateSupervisorInput,
} from "@zhuohui/contracts";

const API_URL = "/api/v1";
let csrfToken = "";

class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request(path: string, init?: RequestInit) {
  const headers = new Headers(init?.headers);
  if (init?.body !== undefined) headers.set("Content-Type", "application/json");
  if (init?.method && !["GET", "HEAD", "OPTIONS"].includes(init.method) && csrfToken) {
    headers.set("x-csrf-token", csrfToken);
  }
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers,
    credentials: "same-origin",
  });
  if (!response.ok) {
    const body = (await response
      .json()
      .catch(() => ({ message: "请求失败" }))) as { message?: string };
    throw new ApiError(body.message ?? "请求失败", response.status);
  }
  if (response.status === 204) return undefined;
  return response.json() as Promise<unknown>;
}

export async function login(username: string, password: string): Promise<AuthUser> {
  const user = authUserSchema.parse(await request("/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  }));
  csrfToken = user.csrfToken;
  return user;
}

export async function getCurrentUser(): Promise<AuthUser> {
  const user = authUserSchema.parse(await request("/auth/me"));
  csrfToken = user.csrfToken;
  return user;
}

export async function logout() {
  await request("/auth/logout", { method: "POST" });
  csrfToken = "";
}

export async function getHermesStatus(): Promise<SyncStatus> {
  return syncStatusSchema.parse(await request("/hermes/status"));
}

export async function syncHermes(): Promise<SyncStatus> {
  return syncStatusSchema.parse(await request("/hermes/sync", {
    method: "POST",
    body: JSON.stringify({}),
  }));
}

export async function getDashboard(): Promise<DashboardResponse> {
  return dashboardResponseSchema.parse(await request("/patrol/dashboard"));
}

export async function runPatrol() {
  return request("/patrol/runs", { method: "POST" });
}

export async function acknowledgeAlerts(
  storeId: string,
  operator = "客服主管",
) {
  return request(`/patrol/alerts/${storeId}/acknowledge`, {
    method: "POST",
    body: JSON.stringify({ operator }),
  });
}

export async function updateSettings(settings: PatrolSettings) {
  return request("/patrol/settings", {
    method: "PUT",
    body: JSON.stringify(settings),
  });
}

export async function createSupervisor(input: CreateSupervisorInput) {
  return request("/patrol/supervisors", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateSupervisor(
  id: string,
  input: UpdateSupervisorInput,
) {
  return request(`/patrol/supervisors/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function deleteSupervisor(id: string) {
  await request(`/patrol/supervisors/${id}`, { method: "DELETE" });
}

export async function assignAgent(agentId: string, supervisorId: string) {
  return request(`/patrol/agents/${agentId}/supervisor`, {
    method: "PUT",
    body: JSON.stringify({ supervisorId }),
  });
}

export function patrolEventsUrl() {
  return `${API_URL}/patrol/events`;
}

export async function getTmallDashboard(): Promise<TmallDashboardResponse> {
  return tmallDashboardResponseSchema.parse(await request("/tmall/dashboard"));
}

export async function syncTmallPerformance(): Promise<TmallDashboardResponse> {
  return tmallDashboardResponseSchema.parse(
    await request("/tmall/sync", { method: "POST" }),
  );
}

export async function importTmallPerformance(
  input: ImportTmallPerformanceInput,
): Promise<TmallDashboardResponse> {
  return tmallDashboardResponseSchema.parse(
    await request("/tmall/imports", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  );
}

export async function clearTmallDemoData(): Promise<TmallDashboardResponse> {
  return tmallDashboardResponseSchema.parse(
    await request("/tmall/demo", { method: "DELETE" }),
  );
}

export async function getTmallExperienceDashboard(): Promise<TmallExperienceDashboardResponse> {
  return tmallExperienceDashboardResponseSchema.parse(
    await request("/tmall/experience/dashboard"),
  );
}

export async function syncTmallExperience(): Promise<TmallExperienceDashboardResponse> {
  return tmallExperienceDashboardResponseSchema.parse(
    await request("/tmall/experience/sync", { method: "POST" }),
  );
}

export async function clearAllDemoData(): Promise<{ cleared: boolean }> {
  return await request("/demo-data", { method: "DELETE" }) as { cleared: boolean };
}

export async function getPddDashboard(): Promise<PddDashboardResponse> {
  return pddDashboardResponseSchema.parse(await request("/pdd/dashboard"));
}
export async function syncPddPerformance(): Promise<PddDashboardResponse> {
  return pddDashboardResponseSchema.parse(await request("/pdd/sync", { method: "POST" }));
}
export async function importPddPerformance(input: ImportPddPerformanceInput): Promise<PddDashboardResponse> {
  return pddDashboardResponseSchema.parse(await request("/pdd/imports", { method: "POST", body: JSON.stringify(input) }));
}
export async function clearPddDemoData(): Promise<PddDashboardResponse> {
  return pddDashboardResponseSchema.parse(await request("/pdd/demo", { method: "DELETE" }));
}

export async function getKuaishouDashboard(): Promise<KuaishouDashboardResponse> {
  return kuaishouDashboardResponseSchema.parse(await request("/kuaishou/dashboard"));
}
export async function syncKuaishouPerformance(): Promise<KuaishouDashboardResponse> {
  return kuaishouDashboardResponseSchema.parse(await request("/kuaishou/sync", { method: "POST" }));
}
export async function importKuaishouPerformance(input: ImportKuaishouPerformanceInput): Promise<KuaishouDashboardResponse> {
  return kuaishouDashboardResponseSchema.parse(await request("/kuaishou/imports", { method: "POST", body: JSON.stringify(input) }));
}
export async function clearKuaishouDemoData(): Promise<KuaishouDashboardResponse> {
  return kuaishouDashboardResponseSchema.parse(await request("/kuaishou/demo", { method: "DELETE" }));
}

export async function getDouyinDashboard(): Promise<DouyinDashboardResponse> {
  return douyinDashboardResponseSchema.parse(await request("/douyin/dashboard"));
}

export async function syncDouyinPerformance(): Promise<DouyinDashboardResponse> {
  return douyinDashboardResponseSchema.parse(
    await request("/douyin/sync", { method: "POST" }),
  );
}

export async function importDouyinPerformance(
  input: ImportDouyinPerformanceInput,
): Promise<DouyinDashboardResponse> {
  return douyinDashboardResponseSchema.parse(
    await request("/douyin/imports", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  );
}

export async function clearDouyinDemoData(): Promise<DouyinDashboardResponse> {
  return douyinDashboardResponseSchema.parse(
    await request("/douyin/demo", { method: "DELETE" }),
  );
}

export async function getDouyinExperienceDashboard(): Promise<DouyinExperienceDashboardResponse> {
  return douyinExperienceDashboardResponseSchema.parse(
    await request("/douyin/experience/dashboard"),
  );
}

export async function syncDouyinExperience(): Promise<DouyinExperienceDashboardResponse> {
  return douyinExperienceDashboardResponseSchema.parse(
    await request("/douyin/experience/sync", { method: "POST" }),
  );
}

export type KnowledgeStatus = "detected" | "draft" | "published";
export type KnowledgeItem = {
  id: string;
  question: string;
  aliases: string[];
  answer: string;
  category: string;
  platforms: string[];
  frequency: number;
  trend: number;
  status: KnowledgeStatus;
  source: "ai" | "manual";
  versions: number;
  updatedAt: string;
};
export type KnowledgeData = {
  items: KnowledgeItem[];
  summary: {
    conversationCount: number;
    lastAnalyzedAt: string | null;
    periodDays: number;
  };
};

export async function getKnowledge() {
  return request("/knowledge/") as Promise<KnowledgeData>;
}

export async function createKnowledgeItem(question: string, category: string) {
  return request("/knowledge/items", {
    method: "POST",
    body: JSON.stringify({ question, category }),
  }) as Promise<KnowledgeItem>;
}

export async function updateKnowledgeItem(
  id: string,
  update: Partial<
    Pick<
      KnowledgeItem,
      "question" | "aliases" | "answer" | "category" | "platforms" | "status"
    >
  >,
) {
  return request(`/knowledge/items/${id}`, {
    method: "PATCH",
    body: JSON.stringify(update),
  }) as Promise<KnowledgeItem>;
}

export async function deleteKnowledgeItem(id: string) {
  return request(`/knowledge/items/${id}`, { method: "DELETE" });
}

export async function generateKnowledgeAnswer(id: string, instruction = "") {
  return request(`/knowledge/items/${id}/generate`, {
    method: "POST",
    body: JSON.stringify({ instruction }),
  }) as Promise<KnowledgeItem>;
}

export async function analyzeConversations(
  conversations: string[],
  maxQuestions: number,
  periodDays: number,
) {
  return request("/knowledge/analyze", {
    method: "POST",
    body: JSON.stringify({ conversations, maxQuestions, periodDays }),
  }) as Promise<KnowledgeData>;
}
